import { events, pageViewTypes } from "@constants/tracking";
import { AppBar } from "@features/dashboard/layouts/AppBar";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useGoToReviewSession } from "@features/report/hooks/useGoToReviewSession";
import { useReport } from "@features/report/hooks/useReport";
import { useReportAnalytics } from "@features/report/hooks/useReportAnalytics";
import { useSessionReportItemTypeDetailDialog } from "@features/report/hooks/useSessionReportItemTypeDetailDialog";
import { useSessionItems } from "@features/session/hooks/useSessionItems";
import useSessionStore from "@features/session/hooks/useSessionStore";
import useAnalytics from "@hooks/useAnalytics";
import {
	type SkillFilter,
	isReportSummativeTest,
} from "@memorang/helpers/report";
import { isProficiencyTag, isSkillTag } from "@memorang/helpers/tag";
import Box from "@memorang/ui/components/Box";
import { ConnectedButtonGroup } from "@memorang/ui/components/ConnectedButtonGroup";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { SessionReportCard } from "@memorang/ui/components/SessionReport/SessionReportCard";
import { SessionReportCardSkeleton } from "@memorang/ui/components/SessionReport/SessionReportCardSkeleton";
import { SessionReportQuestionTypeRowList } from "@memorang/ui/components/SessionReport/SessionReportQuestionTypeCard/SessionReportQuestionTypeRowList";
import {
	buildSessionReportQuestionTypeRowsWithItems,
	groupQuestionItemsByQuestionType,
	resolveFilter,
} from "@memorang/ui/components/SessionReport/SessionReportQuestionTypeCard/helpers";
import { useDevice } from "@memorang/ui/useDevice";
import DocumentTitle from "components/DocumentTitle";
import {
	Redirect,
	useGlobalSearchParams,
	useLocalSearchParams,
	useRouter,
} from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: report types route with filters + review wiring
export default function SessionReportTypesRoute() {
	useAnalytics(
		useMemo(
			() => ({
				type: pageViewTypes.reportTypes,
			}),
			[],
		),
	);

	const { t } = useTranslation();
	const router = useRouter();
	const { id: sessionId } = useLocalSearchParams<{ id: string }>();
	const { isMobile } = useDevice();
	const { filter: filterParam } = useGlobalSearchParams<{ filter?: string }>();

	const sessionStoreIsSummativeTest = useSessionStore(
		(state) => state.isSummativeTest,
	);
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();
	const { reportDetails, isReportError, isReportPending } = useReport();
	const { trackReportEvent } = useReportAnalytics({
		reportDetails,
		view: pageViewTypes.reportTypes,
	});

	const isSummativeTest = reportDetails
		? isReportSummativeTest(reportDetails, { studyPackCollectionId })
		: Boolean(sessionStoreIsSummativeTest);

	const { data: sessionItems, isPending: sessionItemsLoading } =
		useSessionItems({
			sessionId: reportDetails?.id ?? 0,
			isSummativeTest,
			enabled: Boolean(reportDetails?.id),
		});

	const { goToReviewSession, isReviewLoading } = useGoToReviewSession({
		sessionId: reportDetails?.id,
		isSummativeTest,
		trackReportEvent,
	});

	const { onSelectQuestionType, itemTypeDetailDialog } =
		useSessionReportItemTypeDetailDialog({
			onReview: goToReviewSession,
			isReviewLoading,
			trackReportEvent,
		});

	const skillTags = useMemo(
		() => (reportDetails?.tagDetails ?? []).filter((tag) => isSkillTag(tag)),
		[reportDetails?.tagDetails],
	);

	const skillTitles = useMemo(
		() => skillTags.map((tag) => tag.title),
		[skillTags],
	);

	const initialFilter = useMemo(
		() => resolveFilter(filterParam, skillTitles),
		[filterParam, skillTitles],
	);

	const [_skillFilter, setSkillFilter] = useState<SkillFilter | undefined>(
		undefined,
	);
	const skillFilter = _skillFilter ?? initialFilter;

	const handleSkillFilterChange = (value: SkillFilter) => {
		trackReportEvent(events.sessionReportFiltered, {
			filterType: "skill",
			value,
		});
		setSkillFilter(value);
	};

	const proficiencyTags = useMemo(
		() =>
			(reportDetails?.tagDetails ?? []).filter((tag) => isProficiencyTag(tag)),
		[reportDetails?.tagDetails],
	);

	const filterButtons = useMemo(() => {
		const style = {
			flexGrow: 1,
			flexShrink: 1,
			flexBasis: "auto" as const,
		};
		return [
			{
				value: "all",
				label: t("report.itemTypesFilterAll"),
				style,
			},
			...skillTags.map((tag) => ({
				value: tag.title,
				label: tag.title,
				style,
			})),
		];
	}, [skillTags, t]);

	const { rows, questionItems } = useMemo(() => {
		if (!reportDetails) {
			return { rows: [], questionItems: [] };
		}
		if (!sessionItems) {
			return { rows: [], questionItems: [] };
		}
		return buildSessionReportQuestionTypeRowsWithItems({
			tags: reportDetails.tagDetails,
			sessionItems,
			skillFilter,
		});
	}, [reportDetails, sessionItems, skillFilter]);

	const questionItemsByQuestionType = useMemo(() => {
		if (!sessionItems) {
			return {};
		}
		return groupQuestionItemsByQuestionType(questionItems, sessionItems);
	}, [questionItems, sessionItems]);

	if (isReportError || !(isReportPending || reportDetails)) {
		return (
			<Redirect
				href={{
					pathname: "/session/[id]/report" as const,
					params: { id: sessionId },
				}}
			/>
		);
	}

	const handleNavigateBack = () => {
		if (router.canGoBack()) {
			router.back();
			return;
		}
		router.push({
			pathname: "/session/[id]/report",
			params: { id: reportDetails?.id ?? 0 },
		});
	};

	const handleNavigateToReport = () => {
		if (!reportDetails) {
			return;
		}
		router.push({
			pathname: "/session/[id]/report",
			params: { id: reportDetails.id },
		});
	};

	const breadcrumbs = reportDetails
		? [
				{
					label: `${reportDetails.examName} - ${reportDetails.id}`,
					action: handleNavigateToReport,
				},
				{
					label: t("report.itemTypesTitle"),
				},
			]
		: undefined;

	const isLoading =
		isReportPending || !reportDetails ? true : sessionItemsLoading;

	return (
		<>
			<DocumentTitle title={t("report.itemTypesTitle")} />
			<AppBar
				title={t("report.itemTypesTitle")}
				breadcrumbs={breadcrumbs}
				onPressBack={handleNavigateBack}
				hideCommandBar
				hideExamSwitcher
			/>
			<Box flex={1}>
				<ScrollContainer maxWidth="sm">
					<Box
						paddingTop={16}
						paddingBottom={isMobile ? 24 : 64}
						paddingHorizontal={isMobile ? 0 : 32}
						gap={16}
					>
						<Box
							flexDirection={isMobile ? "column" : "row"}
							alignItems={isMobile ? "stretch" : "center"}
							justifyContent="space-between"
							gap={16}
							paddingHorizontal={isMobile ? 16 : 0}
						>
							{isMobile ? null : (
								<Text variant="headlineSmall">
									{t("report.itemTypesTitle")}
								</Text>
							)}
							{filterButtons.length > 2 ? (
								<ConnectedButtonGroup
									value={skillFilter}
									onValueChange={handleSkillFilterChange}
									buttons={filterButtons}
								/>
							) : null}
						</Box>
						{isLoading ? (
							<SessionReportCardSkeleton />
						) : (
							<SessionReportCard.Frame>
								<SessionReportCard.Body>
									<SessionReportQuestionTypeRowList
										rows={rows}
										questionItemsByQuestionType={questionItemsByQuestionType}
										sessionItems={sessionItems ?? []}
										isSummativeTest={isSummativeTest}
										proficiencyTags={proficiencyTags}
										skill={skillFilter === "all" ? undefined : skillFilter}
										onRowPress={onSelectQuestionType}
									/>
								</SessionReportCard.Body>
							</SessionReportCard.Frame>
						)}
					</Box>
				</ScrollContainer>
			</Box>
			{itemTypeDetailDialog}
		</>
	);
}
